import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  makeStyles,
  TextField,
  Tooltip,
} from "@material-ui/core"
import useAsyncEffect from "use-async-effect"
import { Autocomplete } from "@material-ui/lab"
import InfiniteScroll from "react-infinite-scroll-component"
import { RowBox } from "../Boxes"
import { Caption, FieldSectionHeading } from "../Headings"
import { Icon } from "../Icon"
import { toId, useDebounce } from "../../utils"
import { ADMINABLE_USERS_LIMIT, useMutationIntegrationSetUserAttribute, useQueryAdminableUsers } from "../../data"
import { LoadingSpinner } from "../LoadingSpinner"
import { useQueryIntegrationUserAttributeValues } from "../../data/integrations/useQueryIntegration"

const useAttributeListItemStyles = makeStyles((theme) => ({
  avatar: {
    minWidth: "auto",
    marginRight: theme.spacing(1),
  },
  text: {
    padding: 4,
  },
}))

const getUserAttribute = (user, integration, attribute) =>
  user?.attributes.find((item) => toId(item.integration) === toId(integration) && item.key === attribute.key)?.value ||
  ""

const UserAttributeListItem = ({ integration, user, attribute, onChange }) => {
  const mounted = useRef(false)
  const classes = useAttributeListItemStyles()
  const { data, loading } = useQueryIntegrationUserAttributeValues({
    variables: { id: toId(integration), key: attribute.key },
  })
  const [setAttribute] = useMutationIntegrationSetUserAttribute()
  const [value, setValue] = useState(getUserAttribute(user, integration, attribute))
  const debouncedValue = useDebounce(value, 300)

  useAsyncEffect(async () => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    await setAttribute({
      variables: {
        id: toId(integration),
        user: toId(user),
        key: attribute.key,
        value: debouncedValue.toString(),
      },
    })
    onChange && onChange()
  }, [debouncedValue, attribute])

  useEffect(() => {
    setValue(getUserAttribute(user, integration, attribute))
  }, [attribute, integration, user])

  const handleChange = async (_, newValue) => {
    setValue(newValue?.key || "")
  }

  const optionValue = useMemo(
    () => data?.integration?.userAttributeValues.find((item) => item.key === value) || null,
    [data, value]
  )

  return (
    <ListItem disableGutters alignItems="flex-start">
      <ListItemAvatar classes={{ root: classes.avatar }}>
        <RowBox>
          <Icon name="integration-user" />
        </RowBox>
      </ListItemAvatar>
      <ListItemText
        primaryTypographyProps={{ component: "div" }}
        primary={
          <RowBox alignContent="center">
            <Grid container alignContent="center">
              <Grid item xs={6}>
                <FieldSectionHeading mb={0}>
                  <Tooltip title={toId(user)}>
                    <span>{user.fullName}</span>
                  </Tooltip>
                </FieldSectionHeading>

                <Box>{attribute.name}</Box>
                <Caption mb={0}>{attribute.description}</Caption>
              </Grid>
              <Grid item xs={6}>
                {/* <TextField
                  variant="outlined"
                  placeholder="Not set"
                  value={value}
                  size="small"
                  fullWidth
                  onChange={handleChange}
                /> */}
                <Autocomplete
                  value={optionValue}
                  autoHighlight
                  getOptionLabel={(option) => `${option.value} (#${option.key})`}
                  getOptionSelected={(option) => option.key === optionValue.key}
                  options={data?.integration?.userAttributeValues || []}
                  loading={loading}
                  disabled={loading}
                  onChange={handleChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Not set"
                      fullWidth
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? <LoadingSpinner size={24} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </RowBox>
        }
      />
    </ListItem>
  )
}

const adminableUsersOptions = {
  variables: { offset: 0, status: ["active", "invited"] },
  fetchPolicy: "cache-and-network",
}

const IntegrationUserConfiguration = ({ area, integration }) => {
  const { data, fetchMore } = useQueryAdminableUsers(adminableUsersOptions)
  const [offset, setOffset] = useState(0)
  const {
    integrationProvider: { name, attributes },
  } = integration

  useEffect(() => {
    fetchMore({
      variables: {
        offset,
      },
    })
  }, [fetchMore, offset])

  const items = useMemo(
    () =>
      data?.adminableUsers.filter(
        (item) =>
          area === "user" ||
          (area === "user-unmapped" &&
            (!item.attributes?.length ||
              !attributes.user.every((attribute) =>
                item.attributes.find((ia) => toId(ia.integration) === toId(integration) && ia.key === attribute.key)
              ) ||
              item.attributes?.some(
                (attribute) => toId(attribute.integration) === toId(integration) && !attribute.value
              )))
      ) || [],
    [area, attributes.user, data, integration]
  )

  const loadMore = () => {
    setOffset(offset + ADMINABLE_USERS_LIMIT)
  }

  return (
    <Box m={1}>
      <Caption>The following {name} attributes are mapped to your Operandio users.</Caption>

      <InfiniteScroll dataLength={items.length || 0} next={loadMore} scrollableTarget="CreatorMaster-content" hasMore>
        {items.map((user) => (
          <List key={toId(user)} disablePadding>
            {attributes.user.map((attribute, index) => (
              <React.Fragment key={attribute.key}>
                {index > 0 && <Divider />}
                <UserAttributeListItem integration={integration} user={user} attribute={attribute} />
              </React.Fragment>
            ))}
          </List>
        ))}
      </InfiniteScroll>
      {!data && (
        <Box display="flex" justifyContent="center">
          <LoadingSpinner size={60} />
        </Box>
      )}
    </Box>
  )
}

export { IntegrationUserConfiguration }
